<template>
  <layout-page>
    <tabs-vertical :tab-index="currentTab">
      <b-tab-item
        label="Pipeline Settings"
        value="pipeline"
        icon="view-dashboard"
      >
        <pipeline-tab v-if="currentTab === 'pipeline'"/>
      </b-tab-item>

      <b-tab-item
        label="Lead Settings"
        value="leads"
        icon="home"
      >
        <leads-tab v-if="currentTab === 'leads'"/>
      </b-tab-item>

      <b-tab-item
        label="Follow Up Settings"
        value="followUp"
        icon="account-voice"
      >
        <follow-up-sequence-tab v-if="currentTab === 'followUp'"/>
      </b-tab-item>

      <b-tab-item
        label="Contact Settings"
        value="contacts"
        icon="account-multiple"
      >
        <contacts-tab v-if="currentTab === 'contacts'"/>
      </b-tab-item>

      <b-tab-item
        label="Integrations"
        value="integrations"
        icon="video-input-component"
      >
        <integrations-tab v-if="currentTab === 'integrations'"/>
      </b-tab-item>

      <b-tab-item
        label="Email Settings"
        value="email"
        icon="email"
      >
        <email-settings-not-available-tab v-if="!hasEmailIntegrationAbility"/>
        <integration-custom-domain v-else-if="emailIntegrationType === ENUMS.EmailIntegrationTypeEnum.MailGun.value"/>
        <integration-gmail v-else-if="emailIntegrationType === ENUMS.EmailIntegrationTypeEnum.Gmail.value"/>
        <email-settings-choice-tab v-else/>
      </b-tab-item>

      <b-tab-item
        label="Webhooks"
        value="webhooks"
        icon="link"
      >
        <webhooks-tab v-if="currentTab === 'webhooks'"/>
      </b-tab-item>

      <b-tab-item
        label="Call Scripts"
        value="scripts"
        icon="clipboard-outline"
      >
        <call-scripts-tab v-if="currentTab === 'scripts'"/>
      </b-tab-item>

      <b-tab-item
        label="Snippets"
        value="snippets"
        icon="clipboard-text"
      >
        <snippets-tab v-if="currentTab === 'snippets'"/>
      </b-tab-item>

      <b-tab-item
        label="Task Settings"
        value="task"
        icon="check"
      >
        <task-setting-tab v-if="currentTab === 'task'"/>
      </b-tab-item>

      <b-tab-item
        label="Appointment Settings"
        value="appointment"
        icon="calendar"
      >
        <appointment-settings-tab v-if="currentTab === 'appointment'"/>
      </b-tab-item>

      <b-tab-item
        v-if="showStoreSettings"
        label="Store Settings"
        value="store"
        icon="storefront"
      >
        <shareable-store-tab v-if="currentTab === 'store'"/>
      </b-tab-item>


      <b-tab-item
        label="Imports"
        value="imports"
        icon="cloud-download"
      >
        <import-tab v-if="currentTab === 'imports'"/>
      </b-tab-item>

      <b-tab-item
        v-if="showExportSettings"
        label="Exports"
        value="exports"
        icon="cloud-upload"
      >
        <exports-tab v-if="currentTab === 'exports'"/>
      </b-tab-item>

    </tabs-vertical>
  </layout-page>
</template>

<script>
import LeadsTab from "./LeadsTab";
import SnippetsTab from "./SnippetsTab";
import ImportTab from "./ImportTab";
import ExportsTab from "./ExportsTab";
import AppointmentSettingsTab from "./AppointmentSettingsTab";
import ContactsTab from "./ContactsTab";
import WebhooksTab from "./WebhooksTab";
import CallScriptsTab from "./CallScriptsTab";
import PipelineTab from "./PipelineTab";
import ShareableStoreTab from "./ShareableStoreTab";
import EmailSettingsChoiceTab from "./EmailSettingsChoiceTab.vue";
import IntegrationCustomDomain from "./integrations/IntegrationCustomDomain.vue";
import IntegrationGmail from "./integrations/IntegrationGmail.vue";
import EmailSettingsNotAvailableTab from "./EmailSettingsNotAvailableTab.vue";
import FollowUpSequenceTab from "./FollowUpSequenceTab";
import IntegrationsTab from "./IntegrationsTab";
import TaskSettingTab from "./TaskSettingTab";

export default {
  name: "SettingsPage",
  components: {
    IntegrationsTab,
    EmailSettingsNotAvailableTab,
    FollowUpSequenceTab,
    IntegrationGmail,
    IntegrationCustomDomain,
    EmailSettingsChoiceTab,
    ShareableStoreTab,
    PipelineTab,
    CallScriptsTab,
    WebhooksTab,
    ContactsTab,
    AppointmentSettingsTab,
    ExportsTab,
    ImportTab,
    SnippetsTab,
    LeadsTab,
    TaskSettingTab,
  },
  metaInfo() {
    return {
      title: this.metaTitle,
    };
  },
  data() {
    return {
      currentTab: "pipeline"
    };
  },
  computed: {
    metaTitle() {
      switch (this.currentTab) {
        case "pipeline":
          return "Pipeline Settings";
        case "leads":
          return "Leads Settings";
        case "followUp":
          return "Follow Up Settings";
        case "contacts":
          return "Contact Settings";
        case "phone":
          return "Phone Settings";
        case "email":
          return "Email Settings";
        case "integrations":
          return "Integrations";
        case "webhooks":
          return "Webhook Settings";
        case "scripts":
          return "Call Script Settings";
        case "snippets":
          return "Snippet Settings";
        case "task":
          return "Task Settings";
        case "appointment":
          return "Appointment Settings";
        case "store":
          return "Store Settings";
        case "imports":
          return "Import Settings";
        case "exports":
          return "Export Settings";
        default:
          return "Settings";
      }
    },
    hasEmailIntegrationAbility() {
      return this.$store.getters.account.has_email_integration_feature;
    },
    emailIntegrationType() {
      return this.$store.getters.market.email_integration_type;
    },
    showStoreSettings() {
      return this.$store.state.activeUser.is_owner;
    },
    showExportSettings() {
      return this.$store.state.activeUser.is_owner || this.$store.state.activeUser.can_export_leads;
    }
  },
  watch: {
    $route() {
      this.setTab();
    },
  },
  created() {
    this.setTab();
  },
  methods: {
    setTab() {
      const tabParam = this.$route.params.tab;

      if (tabParam) {
        this.currentTab = tabParam;

        // Want to remove the page param when they leave integrations page
        if(tabParam !== 'integrations') {
          this.$router.replace({
            params: {page: null},
            query: this.$route.query
          });
        }
      } else {
        this.$router.replace({
          params: {tab: this.currentTab},
          query: this.$route.query
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

</style>
